import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { v4 as uuidv4 } from 'uuid';
import { ViewType, Promotion, PromotionStatus } from './types';
import { ViewSwitcher } from './ViewSwitcher';
import { StatsBar } from './StatsBar';
import { PromotionForm } from './PromotionForm';
import { CardDashboardView } from './views/CardDashboardView';
import { KanbanView } from './views/KanbanView';
import { TimelineView } from './views/TimelineView';
import { Wallet } from 'lucide-react';
const SAMPLE_DATA: Promotion[] = [{
  id: '1',
  bankName: 'Chase',
  promotionName: 'Total Checking $300',
  minAmount: 500,
  deadline: '2024-12-31',
  bonusDate: '2025-01-15',
  bonusAmount: 300,
  status: 'active',
  accountType: 'Checking',
  notes: 'Direct deposit required'
}, {
  id: '2',
  bankName: 'Wells Fargo',
  promotionName: 'Everyday Checking $325',
  minAmount: 1000,
  deadline: '2024-11-15',
  bonusDate: '2025-02-01',
  bonusAmount: 325,
  status: 'active',
  accountType: 'Checking',
  notes: 'Keep balance for 90 days'
}, {
  id: '3',
  bankName: 'Capital One',
  promotionName: '360 Performance Savings',
  minAmount: 10000,
  deadline: '2025-01-01',
  bonusDate: '2025-04-01',
  bonusAmount: 1000,
  status: 'researching',
  accountType: 'Savings'
} as any, {
  id: '4',
  bankName: 'Citi',
  promotionName: 'Priority Account',
  minAmount: 15000,
  deadline: '2024-10-01',
  bonusDate: '2024-12-01',
  bonusAmount: 500,
  status: 'applied',
  accountType: 'Checking'
}, {
  id: '5',
  bankName: 'Amex',
  promotionName: 'High Yield Savings',
  minAmount: 25000,
  deadline: '2024-09-01',
  bonusDate: '2024-11-01',
  bonusAmount: 350,
  status: 'completed',
  accountType: 'Savings'
}];
export function BankPromotionsTracker() {
  const [currentView, setCurrentView] = useState<ViewType>('cards');
  const [promotions, setPromotions] = useState<Promotion[]>(() => {
    const saved = localStorage.getItem('bankPromotions');
    return saved ? JSON.parse(saved) : SAMPLE_DATA;
  });
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  useEffect(() => {
    localStorage.setItem('bankPromotions', JSON.stringify(promotions));
  }, [promotions]);
  const handleSave = (data: Promotion | Omit<Promotion, 'id'>) => {
    if ('id' in data) {
      setPromotions(promotions.map(p => p.id === data.id ? data : p));
    } else {
      setPromotions([...promotions, {
        ...data,
        id: uuidv4()
      }]);
    }
    setEditingId(null);
  };
  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this promotion?')) {
      setPromotions(promotions.filter(p => p.id !== id));
    }
  };
  const handleStatusChange = (id: string, status: PromotionStatus) => {
    setPromotions(promotions.map(p => p.id === id ? {
      ...p,
      status
    } : p));
  };
  const handleEdit = (id: string) => {
    setEditingId(id);
    setIsFormOpen(true);
  };
  const handleAddNew = () => {
    setEditingId(null);
    setIsFormOpen(true);
  };
  return <div className="min-h-screen bg-gray-50 text-gray-900 font-sans pb-20" data-id="element-1">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30" data-id="element-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-3">
          <div className="flex items-center gap-3" data-id="element-4">
            <div className="bg-blue-600 p-2 rounded-lg text-white" data-id="element-5">
              <Wallet className="w-5 h-5" data-id="element-6" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 tracking-tight" data-id="element-7">
              BankBonus<span className="text-blue-600" data-id="element-8">Tracker</span>
            </h1>
          </div>

          <ViewSwitcher currentView={currentView} onViewChange={setCurrentView} data-id="element-9" />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-10">
        <StatsBar promotions={promotions} data-id="element-11" />

        <AnimatePresence mode="wait" data-id="element-12">
          <motion.div key={currentView} initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.2
        }} data-id="element-13">
            {currentView === 'cards' && <CardDashboardView promotions={promotions} onEdit={handleEdit} onDelete={handleDelete} onStatusChange={handleStatusChange} onAddNew={handleAddNew} data-id="element-14" />}
            {currentView === 'kanban' && <KanbanView promotions={promotions} onEdit={handleEdit} onDelete={handleDelete} onStatusChange={handleStatusChange} data-id="element-15" />}
            {currentView === 'timeline' && <TimelineView promotions={promotions} onEdit={handleEdit} data-id="element-16" />}
          </motion.div>
        </AnimatePresence>
      </main>

      <PromotionForm isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} onSave={handleSave} initialData={editingId ? promotions.find(p => p.id === editingId) : undefined} data-id="element-17" />
    </div>;
}